import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as helper from "../helpers/reducerHelper";
import * as translationHelper from "../helpers/translationHelper";
import * as windowService from "../services/windowService";
import * as getterHelper from "../helpers/getterHelper";
import * as modelHelper from "../helpers/modelHelper";

import tutorialList from "../resources/data/tutorial.json";

import Tab from "../models/gui/Tab";
import Modal from "../models/gui/Modal";
import TutorialManager from "../models/TutorialManager";

const initialState = {
    status: componentTypes.APPLICATION_STATUS_RUNNING,
    activeTab: componentTypes.DEFAULT_TAB_ID,
    activeTabType: null,
    activeTabScene: null,
    activeMenu: false,
    activeMenuItem: null,
    activeSubmenuItem: null,
    activeSettingsMenuContent: null,
    activeHelp: windowService.getBasicSetup() === false,
    activeDialog: null,
    screenSaver: false,
    tabList: [],
    modalList: [],
    flashMessages: [],
    flashMessagesStatus: null,
    messageDialogList: [],
    customGlobalSearch: false,
    settingsMenu: null,
    blocked: false,
    rendered: false,
    imageStore: 0,
    windowWidth: windowService.getWindowWidth(),
    activeNotificationPanel: false,
    tutorial: new TutorialManager(tutorialList, windowService.getTutorial(), null)
};

/**
 * Structure of navigation store:
 * - status {String}                            - indicates status of initialization or determination of Electron window
 * - activeTab {Number}                         - ID of active tab
 * - activeTabType {String | null}              - Type of active tab
 * - activeTabScene {String | null}             - Subscene of active tab
 * - activeMenu {Boolean}                       - if true, left menu is opened
 * - activeMenuItem {String}                    - key of selected menu item
 * - activeSubmenuItem {String}                 - key of selected submenu item
 * - activeSettingsMenu {Array | null}          - subarray with active settings menu structure
 * - activeSettingsMenuContent {Array | null}   - subarray with active settings menu content
 * - activeDialog {String}                      - if it is not null, machine or spectro dialog is opened
 * - screenSaver (Boolean)                      - if true, screen saver is active
 * - tabList {Array}                            - array of opened tabs
 * - modalList {Array}                          - array of opened modals
 * - flashMessages {Array}                      - array of flash info, success, warning and error messages
 * - flashMessagesStatus {String | null}        - status of flash message with the highest priority
 * - messageDialogList {Array}                  - array of dialog messages
 * - customGlobalSearch {Boolean}
 * - settingsMenu {Array}                       - array of MenuItem and SettingsItem objects, defining menu layout
 * - blocked {Boolean}                          - if true, user interface is locked until service window dialog disappears
 * - rendered {Boolean}                         - if true, global requests are loaded and GUI is rendered (zoom is applied)
 * - imageStore {Number}                        - changed value on image store change to trigger component render method
 * - windowWidth {Number}                       - width of window
 * - activeNotificationPanel {Boolean}          - if true, notification panel is opened
 * - tutorial {Array}                           - set of objects with intro tutorials
 */
export const navigationReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.INIT_TAB_LIST:
            if (action.payload.tabList.length === 0) {
                return state;
            }

            let activeTab = action.payload.tabList[action.payload.tabList.length - 1];

            return {
                ...state,
                activeTab: activeTab.id,
                activeTabType: activeTab.type,
                activeTabScene: activeTab.scene,
                tabList: action.payload.tabList
            };
        case actionTypes.LOG_IN_SUCCESS:
        case actionTypes.CHANGE_USER_SUCCESS:
            return {
                ...state,
                settingsMenu: action.payload.settingsMenu,
                activeSettingsMenuContent: getterHelper.getActiveSettingsMenuContent(
                    action.payload.settingsMenu,
                    state.activeSubmenuItem !== null ? state.activeSubmenuItem : state.activeMenuItem
                )
            };
        case actionTypes.SAVE_NEW_TAB:
            let tabExists = helper.getTabById(state.tabList, action.payload.tabId);

            if (tabExists === null) {
                return {
                    ...state,
                    activeTab: action.payload.tabId,
                    activeTabType: action.payload.type,
                    activeTabScene: action.payload.scene,
                    activeMenu: false,
                    tabList: state.tabList.concat([
                        new Tab(
                            action.payload.tabId,
                            translationHelper.translateTabName(action.payload.type),
                            action.payload.type,
                            null,
                            state.tabList.length + 1,
                            action.payload.scene
                        )
                    ])
                };
            }

            if (tabExists.scene === action.payload.scene) {
                return {
                    ...state,
                    activeTab: action.payload.tabId,
                    activeTabType: action.payload.type,
                    activeTabScene: action.payload.scene
                };
            }

            return {
                ...state,
                activeTab: action.payload.tabId,
                activeTabType: action.payload.type,
                activeTabScene: action.payload.scene,
                activeMenu: false,
                tabList: helper.updateTabSceneById(state.tabList, action.payload.tabId, action.payload.scene)
            };
        case actionTypes.SAVE_TAB_OR_OPEN_EXISTING_TAB:
            let existingTab = helper.getLastTabByType(state.tabList, action.payload.type);

            if (existingTab !== null) {
                return {
                    ...state,
                    activeTab: existingTab.id,
                    activeTabType: existingTab.type,
                    activeTabScene: existingTab.scene,
                    activeMenu: false
                };
            }

            return {
                ...state,
                activeTab: action.payload.tabId,
                activeTabType: action.payload.type,
                activeTabScene: action.payload.scene,
                activeMenu: false,
                tabList: state.tabList.concat([
                    new Tab(
                        action.payload.tabId,
                        translationHelper.translateTabName(action.payload.type),
                        action.payload.type,
                        null,
                        state.tabList.length + 1,
                        action.payload.scene
                    )
                ])
            };
        case actionTypes.SET_ACTIVE_TAB:
            let nextTab = helper.getTabById(state.tabList, action.payload.tabId);

            if (nextTab === null) {
                return state;
            }

            return {
                ...state,
                activeTab: nextTab.id,
                activeTabType: nextTab.type,
                activeTabScene: nextTab.scene
            };
        case actionTypes.SET_ACTIVE_TAB_SCENE:
            let nextTabScene = helper.getTabById(state.tabList, action.payload.tabId);

            if (nextTabScene === null) {
                return state;
            }

            return {
                ...state,
                activeTabScene: action.payload.scene,
                tabList: helper.updateTabSceneById(state.tabList, action.payload.tabId, action.payload.scene)
            };
        case actionTypes.SET_ACTIVE_TAB_AND_SCENE:
            let nextTabAndScene = helper.getTabById(state.tabList, action.payload.tabId);

            if (nextTabAndScene === null) {
                return state;
            }

            return {
                ...state,
                activeTab: nextTabAndScene.id,
                activeTabType: nextTabAndScene.type,
                activeTabScene: action.payload.scene,
                activeMenu: false,
                tabList: helper.updateTabSceneById(state.tabList, action.payload.tabId, action.payload.scene)
            };
        case actionTypes.CLOSE_ACTIVE_TAB:
            let openedTab = helper.getTabById(action.payload.newTabList, action.payload.newActive);

            if (openedTab === null) {
                return state;
            }

            return {
                ...state,
                activeTab: openedTab.id,
                activeTabType: openedTab.type,
                activeTabScene: openedTab.scene,
                tabList: action.payload.newTabList
            };
        case actionTypes.CLOSE_ALL_TABS:
            let defaultTab = helper.getTabById(action.payload.newTabList, componentTypes.DEFAULT_TAB_ID);

            if (defaultTab === null) {
                return state;
            }

            return {
                ...state,
                activeTab: defaultTab.id,
                activeTabType: defaultTab.type,
                activeTabScene: defaultTab.scene,
                tabList: action.payload.newTabList
            };
        case actionTypes.CLOSE_ALL_TABS_EXCEPT_ACTIVE:
            return {
                ...state,
                tabList: action.payload.newTabList
            };
        case actionTypes.CHANGE_TAB_ORDER:
            let updated = state.tabList.map((tab) =>
                action.payload.order[tab.id] ? tab.updateOrder(action.payload.order[tab.id]) : tab
            );

            return {
                ...state,
                tabList: updated.sort((a, b) => a.order - b.order)
            };
        case actionTypes.MENU_SHOW:
            return {
                ...state,
                activeMenu: true
            };
        case actionTypes.MENU_HIDE:
            return {
                ...state,
                activeMenu: false
            };
        case actionTypes.MENU_SET_MENU_ITEM:
            if (action.payload.submenuItem === null) {
                return {
                    ...state,
                    activeMenuItem: action.payload.item,
                    activeSubmenuItem: null,
                    activeSettingsMenuContent: getterHelper.getActiveSettingsMenuContent(
                        state.settingsMenu,
                        action.payload.item
                    )
                };
            }

            return {
                ...state,
                activeMenuItem: action.payload.item,
                activeSubmenuItem: action.payload.submenuItem,
                activeSettingsMenuContent: getterHelper.getActiveSettingsMenuContent(
                    state.settingsMenu,
                    action.payload.submenuItem
                )
            };
        case actionTypes.MENU_SET_SUBMENU_ITEM:
            return {
                ...state,
                activeSubmenuItem: action.payload.submenuItem,
                activeSettingsMenuContent: getterHelper.getActiveSettingsMenuContent(
                    state.settingsMenu,
                    action.payload.submenuItem
                )
            };
        case actionTypes.ADD_NEW_MAIN_TAB:
            let brandNewTabList = [];
            let added = null;

            for (let item of state.tabList) {
                if (item.id === action.payload.tabId) {
                    added = new Tab(
                        item.id,
                        item.color === action.payload.color.rgb ? item.name : action.payload.color.displayName,
                        componentTypes.MAIN_TAB,
                        action.payload.color.rgb,
                        item.order,
                        action.payload.scene
                    );

                    brandNewTabList.push(added);
                } else {
                    brandNewTabList.push(item);
                }
            }

            if (added === null) {
                return {
                    ...state,
                    activeTab: action.payload.tabId,
                    activeTabType: componentTypes.MAIN_TAB,
                    activeTabScene: action.payload.scene,
                    tabList: state.tabList.concat([
                        new Tab(
                            action.payload.tabId,
                            action.payload.color.displayName,
                            componentTypes.MAIN_TAB,
                            action.payload.color.rgb,
                            state.tabList.length + 1,
                            action.payload.scene
                        )
                    ])
                };
            }

            return {
                ...state,
                activeTab: added.id,
                activeTabType: added.type,
                activeTabScene: added.scene,
                tabList: brandNewTabList
            };
        case actionTypes.UPDATE_TAB_NAME:
            return {
                ...state,
                tabList: state.tabList.map((tab) => {
                    if (tab.id !== action.payload.tabId) {
                        return tab;
                    }

                    return new Tab(
                        tab.id,
                        action.payload.color === null
                            ? translationHelper.translateTabName(tab.type)
                            : action.payload.color.displayName,
                        tab.type,
                        action.payload.color === null ? null : action.payload.color.rgb,
                        tab.order
                    );
                })
            };
        case actionTypes.UPDATE_TAB_COLOR:
            return {
                ...state,
                tabList: state.tabList.map((tab) => {
                    if (tab.id !== action.payload.tabId) {
                        return tab;
                    }

                    return new Tab(
                        tab.id,
                        tab.name,
                        tab.type,
                        action.payload.color === null ? null : action.payload.rgb,
                        tab.order
                    );
                })
            };
        case actionTypes.UPDATE_CUSTOM_TAB_NAME:
            return {
                ...state,
                tabList: state.tabList.map((tab) => {
                    if (tab.id !== action.payload.tabId) {
                        return tab;
                    }
                    if (action.payload.name === null) {
                        return new Tab(
                            tab.id,
                            translationHelper.translateTabName(componentTypes.CUSTOM_TAB),
                            tab.type,
                            tab.color,
                            tab.order
                        );
                    }

                    return new Tab(tab.id, action.payload.name, tab.type, tab.color, tab.order);
                })
            };
        case actionTypes.SEARCH_PANEL_INIT:
            return {
                ...state,
                tabList: state.tabList.map((tab) => {
                    if (tab.id !== action.payload.tabId) {
                        return tab;
                    }

                    return tab.updateNameAndColor(translationHelper.translateTabName(tab.type), null);
                })
            };
        case actionTypes.SETTINGS_SAVE_LOCALE:
            return {
                ...state,
                tabList: state.tabList.map((tab) => {
                    return new Tab(
                        tab.id,
                        tab.color === null ? translationHelper.translateTabName(tab.type) : tab.name,
                        tab.type,
                        tab.color,
                        tab.order
                    );
                })
            };
        case actionTypes.MENU_ADD_NEW_OR_OPEN_MAIN_TAB:
            let staticMainTab = helper.getLastTabByType(state.tabList, componentTypes.MAIN_TAB);

            if (staticMainTab === null) {
                staticMainTab = helper.getLastTabByType(state.tabList, componentTypes.INITIAL_TAB);
            }

            if (staticMainTab !== null) {
                return {
                    ...state,
                    activeTab: staticMainTab.id,
                    activeTabType: staticMainTab.type,
                    activeTabScene: staticMainTab.scene,
                    activeMenu: false
                };
            }

            let newStaticMainTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newStaticMainTabId,
                activeTabType: componentTypes.MAIN_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newStaticMainTabId,
                        translationHelper.translateTabName(componentTypes.MAIN_TAB),
                        componentTypes.MAIN_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_OR_OPEN_COLOR_PALETTE_TAB:
            let staticColorTab = helper.getLastTabByType(state.tabList, componentTypes.COLOR_TAB);

            if (staticColorTab !== null) {
                return {
                    ...state,
                    activeTab: staticColorTab.id,
                    activeTabType: staticColorTab.type,
                    activeTabScene: staticColorTab.scene,
                    activeMenu: false
                };
            }

            let newStaticColorTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newStaticColorTabId,
                activeTabType: componentTypes.COLOR_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newStaticColorTabId,
                        translationHelper.translateTabName(componentTypes.COLOR_TAB),
                        componentTypes.COLOR_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_OR_OPEN_COLOR_SEARCH_TAB:
            let staticColorSearchTab = helper.getLastTabByType(state.tabList, componentTypes.COLOR_SEARCH_TAB);

            if (staticColorSearchTab !== null) {
                return {
                    ...state,
                    activeTab: staticColorSearchTab.id,
                    activeTabType: staticColorSearchTab.type,
                    activeTabScene: staticColorSearchTab.scene,
                    activeMenu: false
                };
            }

            let newStaticColorSearchTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newStaticColorSearchTabId,
                activeTabType: componentTypes.COLOR_SEARCH_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newStaticColorSearchTabId,
                        translationHelper.translateTabName(componentTypes.COLOR_SEARCH_TAB),
                        componentTypes.COLOR_SEARCH_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_OR_OPEN_CUSTOM_TAB:
            let customFormulaTab = helper.getLastTabByType(state.tabList, componentTypes.CUSTOM_TAB);

            if (customFormulaTab !== null) {
                return {
                    ...state,
                    activeTab: customFormulaTab.id,
                    activeTabType: customFormulaTab.type,
                    activeTabScene: customFormulaTab.scene,
                    activeMenu: false
                };
            }

            let newCustomFormulaTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newCustomFormulaTabId,
                activeTabType: componentTypes.CUSTOM_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newCustomFormulaTabId,
                        translationHelper.translateTabName(componentTypes.CUSTOM_TAB),
                        componentTypes.CUSTOM_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.CLONE_MAIN_TO_CUSTOM_TAB:
        case actionTypes.CLONE_MATCHING_TO_CUSTOM_TAB:
        case actionTypes.CLONE_HISTORY_TO_CUSTOM_TAB:
            let lastCustomFormulaTab = helper.getLastTabByType(state.tabList, componentTypes.CUSTOM_TAB);

            if (lastCustomFormulaTab !== null) {
                return {
                    ...state,
                    activeTab: lastCustomFormulaTab.id,
                    activeTabType: lastCustomFormulaTab.type,
                    activeTabScene: lastCustomFormulaTab.scene,
                    activeMenu: false
                };
            }

            return {
                ...state,
                activeTab: action.payload.tabId,
                activeTabType: componentTypes.CUSTOM_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        action.payload.tabId,
                        translationHelper.translateTabName(componentTypes.CUSTOM_TAB),
                        componentTypes.CUSTOM_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_FAVORITES_TAB:
            let favoritesTab = helper.getTabByType(state.tabList, componentTypes.FAVORITES_TAB);

            if (favoritesTab !== null) {
                return {
                    ...state,
                    activeTab: favoritesTab.id,
                    activeTabType: favoritesTab.type,
                    activeTabScene: favoritesTab.scene,
                    activeMenu: false
                };
            }

            let newFavoritesTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newFavoritesTabId,
                activeTabType: componentTypes.FAVORITES_TAB,
                activeTabScene: null,
                tabList: state.tabList
                    .concat([
                        new Tab(
                            newFavoritesTabId,
                            translationHelper.translateTabName(componentTypes.FAVORITES_TAB),
                            componentTypes.FAVORITES_TAB,
                            null,
                            0
                        )
                    ])
                    .sort(function (a, b) {
                        return a.order - b.order;
                    }),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_CANISTERS_TAB:
            let canistersTab = helper.getTabByType(state.tabList, componentTypes.CANISTERS_TAB);

            if (canistersTab !== null) {
                return {
                    ...state,
                    activeTab: canistersTab.id,
                    activeTabType: canistersTab.type,
                    activeTabScene: canistersTab.scene,
                    activeMenu: false
                };
            }

            let newCanistersTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newCanistersTabId,
                activeTabType: componentTypes.CANISTERS_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newCanistersTabId,
                        translationHelper.translateTabName(componentTypes.CANISTERS_TAB),
                        componentTypes.CANISTERS_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_SPECTRO_TAB:
            let spectroTab = helper.getTabByType(state.tabList, componentTypes.SPECTRO_TAB);

            if (spectroTab !== null) {
                return {
                    ...state,
                    activeTab: spectroTab.id,
                    activeTabType: spectroTab.type,
                    activeTabScene: spectroTab.scene,
                    activeMenu: false
                };
            }

            let newSpectroTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newSpectroTabId,
                activeTabType: componentTypes.SPECTRO_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newSpectroTabId,
                        translationHelper.translateTabName(componentTypes.SPECTRO_TAB),
                        componentTypes.SPECTRO_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_STATISTICS_TAB:
            let statisticsTab = helper.getTabByType(state.tabList, componentTypes.STATISTICS_TAB);

            if (statisticsTab === null) {
                let newStatisticsTabId = helper.getNewTabId(state.tabList);

                return {
                    ...state,
                    activeTab: newStatisticsTabId,
                    activeTabType: componentTypes.STATISTICS_TAB,
                    activeTabScene: null,
                    tabList: state.tabList.concat([
                        new Tab(
                            newStatisticsTabId,
                            translationHelper.translateTabName(componentTypes.STATISTICS_TAB),
                            componentTypes.STATISTICS_TAB,
                            null,
                            state.tabList.length + 1,
                            componentTypes.TABLE_SCENE
                        )
                    ]),
                    activeMenu: false
                };
            }

            if (statisticsTab.scene === componentTypes.TABLE_SCENE) {
                return {
                    ...state,
                    activeTab: statisticsTab.id,
                    activeTabType: statisticsTab.type,
                    activeTabScene: statisticsTab.scene,
                    activeMenu: false
                };
            }

            return {
                ...state,
                activeTab: statisticsTab.id,
                activeTabType: statisticsTab.type,
                activeTabScene: componentTypes.TABLE_SCENE,
                activeMenu: false,
                tabList: helper.updateTabSceneById(state.tabList, statisticsTab.id, componentTypes.TABLE_SCENE)
            };
        case actionTypes.MENU_ADD_NEW_CUSTOMERS_TAB:
            let customersTab = helper.getTabByType(state.tabList, componentTypes.CUSTOMERS_TAB);

            if (customersTab !== null) {
                return {
                    ...state,
                    activeTab: customersTab.id,
                    activeTabType: customersTab.type,
                    activeTabScene: customersTab.scene,
                    activeMenu: false
                };
            }

            let newCustomersTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newCustomersTabId,
                activeTabType: componentTypes.CUSTOMERS_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newCustomersTabId,
                        translationHelper.translateTabName(componentTypes.CUSTOMERS_TAB),
                        componentTypes.CUSTOMERS_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_MY_ORDERS_TAB:
            let myOrdersTab = helper.getTabByType(state.tabList, componentTypes.MY_ORDERS_TAB);

            if (myOrdersTab !== null) {
                return {
                    ...state,
                    activeTab: myOrdersTab.id,
                    activeTabType: myOrdersTab.type,
                    activeTabScene: myOrdersTab.scene,
                    activeMenu: false
                };
            }

            let myOrdersTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: myOrdersTabId,
                activeTabType: componentTypes.MY_ORDERS_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        myOrdersTabId,
                        translationHelper.translateTabName(componentTypes.MY_ORDERS_TAB),
                        componentTypes.MY_ORDERS_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_TO_DISPENSE_TAB:
            let toDispenseTab = helper.getTabByType(state.tabList, componentTypes.TO_DISPENSE_TAB);

            if (toDispenseTab === null) {
                let toDispenseTabId = helper.getNewTabId(state.tabList);

                return {
                    ...state,
                    activeTab: toDispenseTabId,
                    activeTabType: componentTypes.TO_DISPENSE_TAB,
                    activeTabScene: componentTypes.TABLE_SCENE,
                    tabList: state.tabList.concat([
                        new Tab(
                            toDispenseTabId,
                            translationHelper.translateTabName(componentTypes.TO_DISPENSE_TAB),
                            componentTypes.TO_DISPENSE_TAB,
                            null,
                            state.tabList.length + 1,
                            componentTypes.TABLE_SCENE
                        )
                    ]),
                    activeMenu: false
                };
            }

            if (toDispenseTab.scene === componentTypes.TABLE_SCENE) {
                return {
                    ...state,
                    activeTab: toDispenseTab.id,
                    activeTabType: toDispenseTab.type,
                    activeTabScene: toDispenseTab.scene,
                    activeMenu: false
                };
            }

            return {
                ...state,
                activeTab: toDispenseTab.id,
                activeTabType: toDispenseTab.type,
                activeTabScene: componentTypes.TABLE_SCENE,
                activeMenu: false,
                tabList: helper.updateTabSceneById(state.tabList, toDispenseTab.id, componentTypes.TABLE_SCENE)
            };
        case actionTypes.MENU_ADD_NEW_BUG_REPORT_TAB:
            let bugReportTab = helper.getTabByType(state.tabList, componentTypes.BUG_REPORT_TAB);

            if (bugReportTab !== null) {
                return {
                    ...state,
                    activeTab: bugReportTab.id,
                    activeTabType: bugReportTab.type,
                    activeTabScene: bugReportTab.scene,
                    activeMenu: false
                };
            }

            let newBugReportTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newBugReportTabId,
                activeTabType: componentTypes.BUG_REPORT_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newBugReportTabId,
                        translationHelper.translateTabName(componentTypes.BUG_REPORT_TAB),
                        componentTypes.BUG_REPORT_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_ORDERS_HISTORY_TAB:
            let orderHistoryTab = helper.getTabByType(state.tabList, componentTypes.ORDERS_HISTORY_TAB);

            if (orderHistoryTab !== null) {
                return {
                    ...state,
                    activeTab: orderHistoryTab.id,
                    activeTabType: orderHistoryTab.type,
                    activeTabScene: orderHistoryTab.scene,
                    activeMenu: false
                };
            }

            let newOrderHistoryTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newOrderHistoryTabId,
                activeTabType: componentTypes.ORDERS_HISTORY_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newOrderHistoryTabId,
                        translationHelper.translateTabName(componentTypes.ORDERS_HISTORY_TAB),
                        componentTypes.ORDERS_HISTORY_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_GLOBAL_SEARCH_TAB:
            let globalSearchTab = helper.getTabByType(state.tabList, componentTypes.GLOBAL_SEARCH_TAB);

            if (globalSearchTab !== null) {
                return {
                    ...state,
                    activeTab: globalSearchTab.id,
                    activeTabType: globalSearchTab.type,
                    activeTabScene: globalSearchTab.scene,
                    activeMenu: false
                };
            }

            let newGlobalSearchTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newGlobalSearchTabId,
                activeTabType: componentTypes.GLOBAL_SEARCH_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newGlobalSearchTabId,
                        translationHelper.translateTabName(componentTypes.GLOBAL_SEARCH_TAB),
                        componentTypes.GLOBAL_SEARCH_TAB,
                        null,
                        state.tabList.length + 1
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MENU_ADD_NEW_MATCHING_TAB:
            if (action.payload.overwrite) {
                let matchingTab = helper.getTabByType(state.tabList, componentTypes.MATCHING_TAB);

                if (matchingTab !== null) {
                    return {
                        ...state,
                        activeTab: matchingTab.id,
                        activeTabType: matchingTab.type,
                        activeTabScene: matchingTab.scene,
                        activeMenu: false
                    };
                }
            }

            let newMatchingTabId = action.payload.id ?? helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newMatchingTabId,
                activeTabType: componentTypes.MATCHING_TAB,
                activeTabScene: action.payload.scene,
                tabList: state.tabList.concat([
                    new Tab(
                        newMatchingTabId,
                        translationHelper.translateTabName(componentTypes.MATCHING_TAB, action.payload.scene),
                        componentTypes.MATCHING_TAB,
                        null,
                        state.tabList.length + 1,
                        action.payload.scene ?? null
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.MATCHING_TAB_SET_DRAFT_FORMULA_SCENE:
            let matchingTabForFormulaDraft = helper.getTabById(state.tabList, action.payload.tabId);

            if (matchingTabForFormulaDraft === null) {
                return state;
            }

            return {
                ...state,
                activeTab: matchingTabForFormulaDraft.id,
                activeTabType: matchingTabForFormulaDraft.type,
                activeTabScene: componentTypes.DRAFT_FORMULA_SCENE,
                activeMenu: false,
                tabList: helper.updateTabDataById(
                    state.tabList,
                    action.payload.tabId,
                    translationHelper.translateTabName(componentTypes.MATCHING_TAB, componentTypes.DRAFT_FORMULA_SCENE),
                    action.payload.color,
                    componentTypes.DRAFT_FORMULA_SCENE
                )
            };
        case actionTypes.MATCHING_TAB_SET_CORRECTION_SCENE:
            let matchingTabForCorrection = helper.getTabById(state.tabList, action.payload.tabId);

            if (matchingTabForCorrection === null) {
                return state;
            }

            return {
                ...state,
                activeTab: matchingTabForCorrection.id,
                activeTabType: matchingTabForCorrection.type,
                activeTabScene: componentTypes.AUTO_CORRECTION_SCENE,
                activeMenu: false,
                tabList: helper.updateTabDataById(
                    state.tabList,
                    action.payload.tabId,
                    translationHelper.translateTabName(
                        componentTypes.MATCHING_TAB,
                        componentTypes.AUTO_CORRECTION_SCENE
                    ),
                    null,
                    componentTypes.AUTO_CORRECTION_SCENE
                )
            };
        case actionTypes.MENU_ADD_NEW_TICKETING_TAB:
            let ticketingTab = helper.getTabByType(state.tabList, componentTypes.TICKETING_TAB);

            if (ticketingTab !== null) {
                return {
                    ...state,
                    activeTab: ticketingTab.id,
                    activeTabType: ticketingTab.type,
                    activeTabScene: ticketingTab.scene,
                    activeMenu: false
                };
            }

            let newTicketingTabId = helper.getNewTabId(state.tabList);

            return {
                ...state,
                activeTab: newTicketingTabId,
                activeTabType: componentTypes.TICKETING_TAB,
                activeTabScene: null,
                tabList: state.tabList.concat([
                    new Tab(
                        newTicketingTabId,
                        translationHelper.translateTabName(componentTypes.TICKETING_TAB),
                        componentTypes.TICKETING_TAB,
                        null,
                        state.tabList.length + 1,
                        null
                    )
                ]),
                activeMenu: false
            };
        case actionTypes.NAVIGATION_OPEN_MODAL:
            return {
                ...state,
                modalList: [
                    ...state.modalList,
                    new Modal(
                        helper.getNewModalId(state.modalList),
                        action.payload.type,
                        action.payload.tabId,
                        action.payload.params,
                        action.payload.tabId === componentTypes.MODAL_SETTINGS_TAB
                            ? componentTypes.MODAL_SETTINGS_STYLE
                            : componentTypes.MODAL_DEFAULT_STYLE
                    )
                ]
            };
        case actionTypes.NAVIGATION_OPEN_JUST_ONE_MODAL:
            return {
                ...state,
                modalList: [
                    new Modal(
                        helper.getNewModalId(state.modalList),
                        action.payload.type,
                        action.payload.tabId,
                        action.payload.params,
                        action.payload.style
                    )
                ]
            };
        case actionTypes.NAVIGATION_CLOSE_MODAL:
            let modals = [...state.modalList];
            let lastModal = modals.reverse().find((modal) => modal.type === action.payload.type);

            return {
                ...state,
                modalList: state.modalList.filter((modal) => modal !== lastModal)
            };
        case actionTypes.NAVIGATION_CLOSE_ALL_MODALS:
            return {
                ...state,
                modalList: state.modalList.filter(
                    (item) =>
                        item.type === componentTypes.IMPORT_DATA_MODAL ||
                        item.type === componentTypes.IMPORT_PRICE_DATA_MODAL ||
                        item.type === componentTypes.PARTIAL_UPDATE_MODAL
                )
            };
        case actionTypes.NAVIGATION_CLOSE_ACTIVE_MODAL:
            return {
                ...state,
                modalList: state.modalList.slice(0, -1)
            };
        case actionTypes.LOG_OUT_SUCCESS:
        case actionTypes.WEBSOCKET_CLOSED:
        case actionTypes.DATABASE_CHANGE:
            return {
                ...initialState,
                status: state.status,
                flashMessages: state.flashMessages,
                flashMessagesStatus: state.flashMessagesStatus
            };
        case actionTypes.SAVE_FLASH_MESSAGE:
            if (action.payload.flashMessage === null) {
                return state;
            }

            let newFlashMessages = state.flashMessages.concat([action.payload.flashMessage]);

            return {
                ...state,
                flashMessages: newFlashMessages,
                flashMessagesStatus: getterHelper.getFlashMessagesStatus(newFlashMessages)
            };
        case actionTypes.NAVIGATION_OPEN_SCREENSAVER:
            return {
                ...state,
                screenSaver: true
            };
        case actionTypes.NAVIGATION_CLOSE_SCREENSAVER:
            return {
                ...state,
                screenSaver: false
            };
        case actionTypes.WEBSOCKET_OPENED:
            return {
                ...state,
                status: componentTypes.APPLICATION_STATUS_RUNNING
            };
        case actionTypes.NAVIGATION_CLOSE_REDLIKE_WINDOW:
            return {
                ...state,
                status: componentTypes.APPLICATION_STATUS_CLOSING
            };
        case actionTypes.RESET_SERVER_CONNECTION:
            return {
                ...state,
                status: componentTypes.APPLICATION_STATUS_RESTORING
            };
        case actionTypes.NAVIGATION_SET_NOTIFICATION_PANEL:
            return {
                ...state,
                activeNotificationPanel: action.payload.activeNotificationPanel
            };
        case actionTypes.DELETE_FLASH_MESSAGE:
            let filteredFlashMessages = state.flashMessages.filter(
                (item) => state.flashMessages.indexOf(item) !== action.payload.deletedItem
            );
            return {
                ...state,
                flashMessages: filteredFlashMessages,
                flashMessagesStatus: getterHelper.getFlashMessagesStatus(filteredFlashMessages)
            };
        case actionTypes.DELETE_ALL_FLASH_MESSAGES:
            return {
                ...state,
                flashMessages: [],
                flashMessagesStatus: null
            };
        case actionTypes.INTERGAMMA_SET_OVERRIDE_MODE:
            return {
                ...state,
                activeMenu: false,
                activeMenuItem: null,
                activeSubmenuItem: null,
                activeSettingsMenuContent: null
            };
        case actionTypes.SET_GLOBAL_CUSTOM_SEARCH:
            return {
                ...state,
                customGlobalSearch: action.payload.customSearch
            };
        case actionTypes.SAVE_ACTIVE_HELP:
            return {
                ...state,
                activeHelp: action.payload.activeHelp
            };
        case actionTypes.RCC_WEBSOCKET_CLOSED:
            return {
                ...state,
                blocked: false
            };
        case actionTypes.SET_BLOCKED_UI:
            return {
                ...state,
                blocked: action.payload.blocked
            };
        case actionTypes.SET_RENDER:
            return {
                ...state,
                rendered: action.payload.rendered
            };
        case actionTypes.NAVIGATION_SET_ACTIVE_TUTORIAL:
            if (state.tutorial.active === null) {
                return {
                    ...state,
                    tutorial: state.tutorial.setActive(action.payload.tutorial)
                };
            }

            return state;
        case actionTypes.NAVIGATION_DISABLE_TUTORIAL:
            if (state.tutorial.active === action.payload.tutorial) {
                return {
                    ...state,
                    tutorial: state.tutorial.setActive(null)
                };
            }

            return state;
        case actionTypes.NAVIGATION_FINISH_TUTORIAL:
            if (state.tutorial.active === action.payload.tutorial) {
                return {
                    ...state,
                    tutorial: state.tutorial.finishTutorial(action.payload.tutorial)
                };
            }

            return state;
        case actionTypes.WINDOW_RESIZED:
            return {
                ...state,
                windowWidth: windowService.getWindowWidth()
            };
        case actionTypes.IMAGE_STORE_UPDATE:
            return {
                ...state,
                imageStore: Math.floor(Math.random() * 1000 + 1)
            };
        case actionTypes.MENU_TRANSLATE_KEYWORDS:
            return {
                ...state,
                settingsMenu: modelHelper.setMenuKeyWords(
                    state.settingsMenu,
                    translationHelper.getSettingsKeyWordsTranslation()
                )
            };
        case actionTypes.MACHINE_SET_SELECTION:
            if (action.payload.selected) {
                return state;
            }

            return {
                ...state,
                activeDialog: null
            };
        case actionTypes.MACHINE_SET_CONNECTION:
            if (action.payload.connected) {
                return state;
            }

            return {
                ...state,
                activeDialog: null
            };
        case actionTypes.SPECTRO_SET_CONNECTION:
            return {
                ...state,
                activeDialog: action.payload.connected ? state.activeDialog : null
            };
        case actionTypes.HIDE_ALL_DIALOGS:
            return {
                ...state,
                activeDialog: null
            };
        case actionTypes.OPEN_RCC_DIALOG:
            return {
                ...state,
                activeDialog: componentTypes.RCC_MACHINE_DIALOG
            };
        case actionTypes.TOGGLE_RCC_DIALOG:
            return {
                ...state,
                activeDialog:
                    state.activeDialog === componentTypes.RCC_MACHINE_DIALOG ? null : componentTypes.RCC_MACHINE_DIALOG
            };
        case actionTypes.OPEN_SPECTRO_DIALOG:
            return {
                ...state,
                activeDialog: componentTypes.RCC_SPECTRO_DIALOG
            };
        case actionTypes.TOGGLE_SPECTRO_DIALOG:
            return {
                ...state,
                activeDialog:
                    state.activeDialog === componentTypes.RCC_SPECTRO_DIALOG ? null : componentTypes.RCC_SPECTRO_DIALOG
            };
        case actionTypes.OPEN_MESSAGE_DIALOG:
            if (action.payload.dialog === null) {
                return state;
            }

            return {
                ...state,
                activeDialog: componentTypes.MESSAGE_DIALOG,
                messageDialogList: [action.payload.dialog, ...state.messageDialogList]
            };
        case actionTypes.TOGGLE_MESSAGE_DIALOG:
            return {
                ...state,
                activeDialog:
                    state.activeDialog === componentTypes.MESSAGE_DIALOG ? null : componentTypes.MESSAGE_DIALOG
            };
        case actionTypes.CANCEL_MESSAGE_DIALOG:
            return {
                ...state,
                activeDialog:
                    state.activeDialog === componentTypes.MESSAGE_DIALOG && state.messageDialogList.length === 1
                        ? null
                        : state.activeDialog,
                messageDialogList: state.messageDialogList.slice(1)
            };
        case actionTypes.CANCEL_ALL_MESSAGE_DIALOGS:
            return {
                ...state,
                activeDialog: state.activeDialog === componentTypes.MESSAGE_DIALOG ? null : state.activeDialog,
                messageDialogList: []
            };
        case actionTypes.NAVIGATION_SET_MODAL_PARAMS:
            return {
                ...state,
                modalList: state.modalList.map((modal) => {
                    if (modal.tabId !== action.payload.tabId || modal.type !== action.payload.type) {
                        return modal;
                    }

                    return modal.updateParams({...modal.params, ...action.payload.params});
                })
            };
        default:
            return state;
    }
};
